import Layout from '../components/layout/Layout';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; // ADDED
import { API_BASE } from '../api';

// 🎨 GLOBAL COLORS DEFINITION (Based on Dashboard.css)
const colors = {
    // Dashboard.css Background/Elements
    backgroundDark: '#0f0c29', 
    backgroundMedium: '#302b63', 
    backgroundLight: '#24243e',
    textLight: 'rgba(255, 255, 255, 0.8)',
    borderDark: 'rgba(255, 255, 255, 0.2)',

    // Primary Colors and Gradients used in Dashboard.css
    primary: '#6366F1', // Indigo/Violet (used for orb-1, badge-role, chart-assigned)
    secondary: '#8B5CF6', // Purple/Pink (used for orb-2)
    accent: '#06B6D4', // Cyan (used for orb-3)
    success: '#10B981', // Green (used for orb-4, completion-rate, chart-completed)
    warning: '#F59E0B', // Orange (used for chart-unassigned)
    danger: '#EF4444', // Red (used for error-alert background)

    // Specific gradients simplified to match the colors above
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', // Blue/Violet (close to primary/secondary)
    gradient2: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 
    gradient3: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', 
    gradient4: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' 
};

function CheckInOutRecords() {
    const navigate = useNavigate(); // ADDED
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [message, setMessage] = useState('');
    const [filter, setFilter] = useState({
        date: '',
        user: '',
        status: ''
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage] = useState(20);
    
    // Get user data from actual session storage
    const userId = sessionStorage.getItem('userId') || '';
    const userRole = sessionStorage.getItem('userRole') || '';
    const userName = sessionStorage.getItem('userName') || '';

    // ADDED: Navigation handler
    const handleUserClick = (userId, userName) => {
        navigate('/user-attendance', { 
            state: { 
                userId: userId,
                userName: userName
            }
        });
    };

    useEffect(() => {
        // Check if the user has permission to view this page
        if (userRole === 'admin' || userRole === 'manager') {
            fetchAllRecords();
        }
    }, [userRole]);

    const fetchAllRecords = async () => {
        try {
            setLoading(true);
            setMessage('');

            const response = await fetch(`${API_BASE}/checkinout_api.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    operation: 'get_all_records'
                })
            });

            const data = await response.json();
            
            if (data.success) {
                setRecords(data.records || []);
            } else {
                setMessage('❌ ' + (data.message || 'Failed to fetch records'));
            }
        } catch (error) {
            console.error('❌ Error fetching records:', error);
            setMessage(`❌ Error loading records: Ensure PHP backend is running at ${API_BASE}/checkinout_api.php`);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleRefresh = () => {
        setRefreshing(true);
        fetchAllRecords();
    };

    const formatTime = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const calculateDuration = (checkIn, checkOut) => {
        if (!checkIn || !checkOut) return 'N/A';
        
        const start = new Date(checkIn);
        const end = new Date(checkOut);
        const diffMs = end - start;
        
        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        
        if (hours > 0) {
            return `${hours}h ${minutes}m`;
        } else {
            return `${minutes}m`;
        }
    };

    const getStatusBadge = (checkOutTime) => {
        if (!checkOutTime) {
            return (
                <span className="status-badge" style={styles.statusBadgeActive}>
                    <span className="status-dot" style={{...styles.statusDot, background: colors.warning}}></span>
                    Active
                </span>
            );
        } else {
            return (
                <span className="status-badge" style={styles.statusBadgeCompleted}>
                    <span className="status-dot" style={{...styles.statusDot, background: colors.success}}></span>
                    Completed
                </span>
            );
        }
    };

    const getRoleBadge = (role) => {
        const roleConfig = {
            admin: { background: colors.danger, label: 'Admin' },
            manager: { background: colors.warning, label: 'Manager' },
            operator: { background: colors.primary, label: 'Operator' },
            editor: { background: colors.secondary, label: 'Editor' },
            client: { background: colors.success, label: 'Client' },
            user: { background: colors.accent, label: 'Desginer' }
        };
        
        const config = roleConfig[role] || roleConfig.user;
        
        return (
            <span className="role-badge" style={{...styles.roleBadge, background: config.background}}>
                {config.label}
            </span>
        );
    };

    // Get browser icon based on browser type
    const getBrowserIcon = (browser) => {
        if (!browser) return 'fas fa-question-circle';
        
        const browserLower = browser.toLowerCase();
        if (browserLower.includes('chrome')) return 'fab fa-chrome';
        if (browserLower.includes('firefox')) return 'fab fa-firefox';
        if (browserLower.includes('safari')) return 'fab fa-safari';
        if (browserLower.includes('edge')) return 'fab fa-edge';
        if (browserLower.includes('opera')) return 'fab fa-opera';
        if (browserLower.includes('explorer') || browserLower.includes('ie')) return 'fab fa-internet-explorer';
        return 'fas fa-globe';
    };

    // Get browser color based on browser type
    const getBrowserColor = (browser) => {
        if (!browser) return colors.textLight;
        
        const browserLower = browser.toLowerCase();
        if (browserLower.includes('chrome')) return '#4285F4';
        if (browserLower.includes('firefox')) return '#FF7139';
        if (browserLower.includes('safari')) return '#000000';
        if (browserLower.includes('edge')) return '#0078D7';
        if (browserLower.includes('opera')) return '#FF1B2D';
        if (browserLower.includes('explorer') || browserLower.includes('ie')) return '#0076D7';
        return colors.accent;
    };

    // Filter records based on filter criteria
    const filteredRecords = records.filter(record => {
        const matchesDate = !filter.date || 
            new Date(record.check_in_time).toDateString() === new Date(filter.date).toDateString();
        
        const matchesUser = !filter.user || 
            record.user_name.toLowerCase().includes(filter.user.toLowerCase()) ||
            record.user_id.toString().includes(filter.user);
        
        const matchesStatus = !filter.status || 
            (filter.status === 'active' && !record.check_out_time) ||
            (filter.status === 'completed' && record.check_out_time);
        
        return matchesDate && matchesUser && matchesStatus;
    });

    // Pagination
    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    const currentRecords = filteredRecords.slice(indexOfFirstRecord, indexOfLastRecord);
    const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const exportToCSV = () => {
        if (filteredRecords.length === 0) return;

        const headers = [
            'User ID', 'User Name', 'Role', 'Date', 
            'Check In Time', 'In Location City', 'In Location Country', 'In Device', 'In Browser',
            'Check Out Time', 'Out Location City', 'Out Location Country', 'Out Device', 'Out Browser',
            'Duration', 'Status'
        ];
        
        const csvData = filteredRecords.map(record => {
            const duration = calculateDuration(record.check_in_time, record.check_out_time);
            const status = record.check_out_time ? 'Completed' : 'Active';
            
            return [
                record.user_id,
                record.user_name,
                record.user_role,
                formatDate(record.check_in_time),
                formatTime(record.check_in_time),
                record.check_in_city || 'N/A',
                record.check_in_country || 'N/A',
                record.check_in_device || 'N/A',
                record.check_in_browser || 'N/A',
                record.check_out_time ? formatTime(record.check_out_time) : 'N/A',
                record.check_out_city || 'N/A',
                record.check_out_country || 'N/A',
                record.check_out_device || 'N/A',
                record.check_out_browser || 'N/A',
                duration,
                status
            ];
        });

        const csvContent = [
            headers.join(','),
            ...csvData.map(row => row.map(field => `"${field}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `attendance_records_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    // Restrict access to admin and manager only
    if (userRole !== 'admin' && userRole !== 'manager') {
        return (
            <Layout>
                <div style={styles.accessDeniedContainer}>
                    <div className="access-denied-icon" style={styles.accessDeniedIcon}>
                        <i className="fas fa-shield-alt"></i>
                    </div>
                    <h3 style={styles.accessDeniedTitle}>Access Restricted</h3>
                    <p style={styles.accessDeniedText}>This page is only available for administrators and managers.</p>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div style={{minHeight: '100vh'}}>
                <div className="records-container" style={styles.container}>
                    {/* Animated Background */}
                    <div className="animated-background" style={styles.animatedBackground}>
                        <div className="floating-orb orb-1" style={{...styles.floatingOrb, ...styles.orb1}}></div>
                        <div className="floating-orb orb-2" style={{...styles.floatingOrb, ...styles.orb2}}></div>
                        <div className="floating-orb orb-3" style={{...styles.floatingOrb, ...styles.orb3}}></div>
                        <div className="floating-orb orb-4" style={{...styles.floatingOrb, ...styles.orb4}}></div>
                        <div className="grid-overlay" style={styles.gridOverlay}></div>
                    </div>

                    {/* Header Section */}
                    <div className="records-header" style={styles.header}>
                        <div className="header-content" style={styles.headerContent}>
                            <div className="welcome-section">
                                <div className="greeting" style={styles.greeting}>
                                    <h1 className="page-title" style={styles.pageTitle}>
                                        <i className="fas fa-clipboard-list me-2"></i>
                                        Detailed Attendance Records
                                    </h1>
                                    <div className="welcome-badge" style={styles.welcomeBadge}>
                                        <span className="badge-text" style={styles.badgeText}>Admin Panel</span>
                                        <span className="badge-role" style={styles.badgeRole}>{userRole}</span>
                                    </div>
                                </div>
                                <p className="page-subtitle" style={styles.pageSubtitle}>
                                    Comprehensive tracking and management of user attendance activities
                                </p>
                            </div>
                            <div className="header-actions" style={styles.headerActions}>
                                <button 
                                    className="refresh-btn"
                                    onClick={handleRefresh}
                                    disabled={refreshing}
                                    style={styles.refreshBtn}
                                >
                                    <div className="refresh-icon" style={styles.refreshIcon}>
                                        <i className={`fas fa-sync ${refreshing ? 'spinning' : ''}`}></i>
                                    </div>
                                    <span>{refreshing ? 'Refreshing...' : 'Refresh Data'}</span>
                                </button>
                                <button 
                                    className="export-btn"
                                    onClick={exportToCSV}
                                    disabled={filteredRecords.length === 0}
                                    style={{
                                        ...styles.exportBtn,
                                        opacity: filteredRecords.length === 0 ? 0.6 : 1,
                                        cursor: filteredRecords.length === 0 ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    <div className="export-icon" style={styles.exportIcon}>
                                        <i className="fas fa-file-export"></i>
                                    </div>
                                    <span>Export CSV</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Message Alert */}
                    {message && (
                        <div className="message-alert" style={{
                            ...styles.messageAlert,
                            background: message.includes('✅') 
                                ? 'rgba(16, 185, 129, 0.1)' 
                                : 'rgba(220, 53, 69, 0.1)',
                            border: message.includes('✅') 
                                ? `1px solid ${colors.success}`
                                : `1px solid ${colors.danger}`,
                            color: message.includes('✅') 
                                ? colors.success
                                : colors.danger
                        }}>
                            <div className="alert-icon" style={{...styles.alertIcon, 
                                color: message.includes('✅') 
                                    ? colors.success
                                    : colors.danger
                            }}>
                                <i className={`fas ${message.includes('✅') ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                            </div>
                            <div className="alert-message" style={styles.alertMessage}>
                                <strong>{message.includes('✅') ? 'Success' : 'Error'}</strong>
                                <span style={{color: 'white'}}>{message}</span>
                            </div>
                            <button className="alert-close" onClick={() => setMessage('')} style={styles.alertClose}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                    )}

                    {/* Main Content */}
                    <div className="records-content" style={styles.content}>
                        {/* Statistics Cards */}
                        <div className="stats-section" style={styles.statsSection}>
                            <div className="section-title" style={styles.sectionTitle}>
                                <h2>Overview Dashboard</h2>
                                <p>Attendance statistics and insights</p>
                            </div>
                            <div className="stats-grid" style={styles.statsGrid}>
                                <div className="stat-card" style={{...styles.statCard, background: colors.gradient}}>
                                    <div className="stat-glow" style={styles.statGlow}></div>
                                    <div className="stat-content" style={styles.statContent}>
                                        <div className="stat-icon" style={styles.statIcon}>
                                            <i className="fas fa-users"></i>
                                        </div>
                                        <div className="stat-info" style={styles.statInfo}>
                                            <h3 className="stat-number" style={styles.statNumber}>
                                                {[...new Set(records.map(record => record.user_id))].length}
                                            </h3>
                                            <p className="stat-label" style={styles.statLabel}>Total Users</p>
                                            <span className="stat-trend" style={styles.statTrend}>Unique users tracked</span>
                                        </div>
                                    </div>
                                    <div className="stat-wave" style={styles.statWave}></div>
                                </div>

                                <div className="stat-card" style={{...styles.statCard, background: colors.gradient2}}>
                                    <div className="stat-glow" style={styles.statGlow}></div>
                                    <div className="stat-content" style={styles.statContent}>
                                        <div className="stat-icon" style={styles.statIcon}>
                                            <i className="fas fa-clock"></i>
                                        </div>
                                        <div className="stat-info" style={styles.statInfo}>
                                            <h3 className="stat-number" style={styles.statNumber}>{records.length}</h3>
                                            <p className="stat-label" style={styles.statLabel}>Total Sessions</p>
                                            <span className="stat-trend" style={styles.statTrend}>All time sessions</span>
                                        </div>
                                    </div>
                                    <div className="stat-wave" style={styles.statWave}></div>
                                </div>

                                <div className="stat-card" style={{...styles.statCard, background: colors.gradient3}}>
                                    <div className="stat-glow" style={styles.statGlow}></div>
                                    <div className="stat-content" style={styles.statContent}>
                                        <div className="stat-icon" style={styles.statIcon}>
                                            <i className="fas fa-sign-in-alt"></i>
                                        </div>
                                        <div className="stat-info" style={styles.statInfo}>
                                            <h3 className="stat-number" style={styles.statNumber}>
                                                {records.filter(record => !record.check_out_time).length}
                                            </h3>
                                            <p className="stat-label" style={styles.statLabel}>Active Now</p>
                                            <span className="stat-trend" style={styles.statTrend}>Currently working</span>
                                        </div>
                                    </div>
                                    <div className="stat-wave" style={styles.statWave}></div>
                                </div>

                                <div className="stat-card" style={{...styles.statCard, background: colors.gradient4}}>
                                    <div className="stat-glow" style={styles.statGlow}></div>
                                    <div className="stat-content" style={styles.statContent}>
                                        <div className="stat-icon" style={styles.statIcon}>
                                            <i className="fas fa-check-circle"></i>
                                        </div>
                                        <div className="stat-info" style={styles.statInfo}>
                                            <h3 className="stat-number" style={styles.statNumber}>
                                                {records.filter(record => record.check_out_time).length}
                                            </h3>
                                            <p className="stat-label" style={styles.statLabel}>Completed</p>
                                            <span className="stat-trend" style={styles.statTrend}>Finished sessions</span>
                                        </div>
                                    </div>
                                    <div className="stat-wave" style={styles.statWave}></div>
                                </div>
                            </div>
                        </div>

                        {/* Filters Section */}
                        <div className="filters-section" style={styles.filtersSection}>
                            <div className="filters-card" style={styles.filtersCard}>
                                <div className="card-header" style={styles.cardHeader}>
                                    <h3 style={styles.cardTitle}>
                                        <i className="fas fa-filter me-2"></i>
                                        Filter Records
                                    </h3>
                                </div>
                                <div className="card-body" style={styles.cardBody}>
                                    <div className="filter-grid" style={styles.filterGrid}>
                                        <div className="form-group" style={styles.formGroup}>
                                            <label style={styles.formLabel}>Date</label>
                                            <input
                                                type="date"
                                                style={styles.formInput}
                                                value={filter.date}
                                                onChange={(e) => setFilter({...filter, date: e.target.value})}
                                            />
                                        </div>
                                        <div className="form-group" style={styles.formGroup}>
                                            <label style={styles.formLabel}>User Search</label>
                                            <input
                                                type="text"
                                                style={styles.formInput}
                                                placeholder="Search by name or ID..."
                                                value={filter.user}
                                                onChange={(e) => setFilter({...filter, user: e.target.value})}
                                            />
                                        </div>
                                        <div className="form-group" style={styles.formGroup}>
                                            <label style={styles.formLabel}>Status</label>
                                            <select
                                                style={styles.dropdownSelect}
                                                value={filter.status}
                                                onChange={(e) => setFilter({...filter, status: e.target.value})}
                                            >
                                                <option value="">All Status</option>
                                                <option value="active">Active Sessions</option>
                                                <option value="completed">Completed Sessions</option>
                                            </select>
                                        </div>
                                        <div className="form-group" style={styles.formGroup}>
                                            <label style={styles.formLabel}>&nbsp;</label>
                                            <button
                                                style={styles.clearFiltersBtn}
                                                onClick={() => setFilter({ date: '', user: '', status: '' })}
                                            >
                                                Clear Filters
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Records Table Section */}
                        <div className="records-table-section" style={styles.recordsTableSection}>
                            <div className="table-card" style={styles.tableCard}>
                                <div className="table-card-header" style={styles.tableCardHeader}>
                                    <h3 style={styles.tableCardTitle}>
                                        <i className="fas fa-table me-2"></i>
                                        Complete Attendance Records
                                    </h3>
                                    <div className="table-info" style={styles.tableInfo}>
                                        <span style={styles.tableInfoText}>
                                            Showing {indexOfFirstRecord + 1}-{Math.min(indexOfLastRecord, filteredRecords.length)} of {filteredRecords.length}
                                        </span>
                                    </div>
                                </div>
                                <div className="table-card-body" style={styles.tableCardBody}>
                                    {loading ? (
                                        <div className="loading-state" style={styles.loadingState}>
                                            <div className="loading-animation" style={styles.loadingAnimation}>
                                                <div className="loading-dots" style={styles.loadingDots}>
                                                    <div className="dot" style={styles.dot}></div>
                                                    <div className="dot" style={styles.dot}></div>
                                                    <div className="dot" style={styles.dot}></div>
                                                </div>
                                            </div>
                                            <h3 style={styles.loadingText}>Loading Records</h3>
                                            <p style={styles.loadingSubtext}>Fetching attendance data</p>
                                        </div>
                                    ) : filteredRecords.length === 0 ? (
                                        <div className="empty-state" style={styles.emptyState}>
                                            <div className="empty-icon" style={styles.emptyIcon}>
                                                <i className="fas fa-clipboard-list"></i>
                                            </div>
                                            <h3 style={styles.emptyTitle}>No Records Found</h3>
                                            <p style={styles.emptyText}>No attendance records match your current filter criteria</p>
                                            <button 
                                                style={styles.resetFiltersBtn}
                                                onClick={() => setFilter({ date: '', user: '', status: '' })}
                                            >
                                                Reset Filters
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="table-container" style={styles.tableContainer}>
                                                <table className="records-table" style={styles.recordsTable}>
                                                    <thead>
                                                        <tr>
                                                            {['User', 'Role', 'Date', 'Check In', 'In Location', 'In Device', 'In Browser', 'Check Out', 'Out Location', 'Out Device', 'Out Browser', 'Duration', 'Status'].map((header, index) => (
                                                                <th key={index} style={styles.tableHeader}>
                                                                    {header}
                                                                </th>
                                                            ))}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {currentRecords.map((record, index) => (
                                                            <tr key={index} style={styles.tableRow}>
                                                                {/* MODIFIED: Made user cell clickable */}
                                                                <td style={styles.tableCell}>
                                                                    <div 
                                                                        className="user-info" 
                                                                        style={{...styles.userInfo, cursor: 'pointer'}}
                                                                        onClick={() => handleUserClick(record.user_id, record.user_name)}
                                                                        title={`View all records for ${record.user_name}`}
                                                                    >
                                                                        <div className="user-name" style={styles.userName}>{record.user_name}</div>
                                                                        <div className="user-id" style={styles.userId}>ID: {record.user_id}</div>
                                                                    </div>
                                                                </td>
                                                                <td style={styles.tableCell}>{getRoleBadge(record.user_role)}</td>
                                                                <td style={styles.tableCell}><div className="date-value" style={styles.dateValue}>{formatDate(record.check_in_time)}</div></td>

                                                                <td style={styles.tableCell}><div className="time-value" style={styles.timeValue}>{formatTime(record.check_in_time)}</div></td>
                                                                <td style={styles.tableCell}>
                                                                    <div className="location-value" style={styles.locationValue}>
                                                                        {record.check_in_city && record.check_in_country ? `${record.check_in_city}, ${record.check_in_country}` : 'N/A'}
                                                                    </div>
                                                                </td>
                                                                <td style={styles.tableCell}><div style={styles.deviceValue}>{record.check_in_device || 'N/A'}</div></td>
                                                                <td style={styles.tableCell}>
                                                                    <div className="browser-info" style={styles.browserInfo}>
                                                                        <i 
                                                                            className={getBrowserIcon(record.check_in_browser)} 
                                                                            style={{...styles.browserIcon, color: getBrowserColor(record.check_in_browser)}}
                                                                        ></i>
                                                                        <span style={styles.browserText}>{record.check_in_browser || 'N/A'}</span>
                                                                    </div>
                                                                </td>
                                                                
                                                                <td style={styles.tableCell}>
                                                                    {record.check_out_time ? <div className="time-value" style={styles.timeValue}>{formatTime(record.check_out_time)}</div> : <span style={styles.emptyValue}>-</span>}
                                                                </td>
                                                                <td style={styles.tableCell}>
                                                                    {record.check_out_city && record.check_out_country ? 
                                                                        <div className="location-value" style={styles.locationValue}>{`${record.check_out_city}, ${record.check_out_country}`}</div> : <span style={styles.emptyValue}>-</span>}
                                                                </td>
                                                                <td style={styles.tableCell}>
                                                                    {record.check_out_device ? <div style={styles.deviceValue}>{record.check_out_device}</div> : <span style={styles.emptyValue}>-</span>}
                                                                </td>
                                                                <td style={styles.tableCell}>
                                                                    {record.check_out_browser ? (
                                                                        <div className="browser-info" style={styles.browserInfo}>
                                                                            <i 
                                                                                className={getBrowserIcon(record.check_out_browser)} 
                                                                                style={{...styles.browserIcon, color: getBrowserColor(record.check_out_browser)}}
                                                                            ></i>
                                                                            <span style={styles.browserText}>{record.check_out_browser}</span>
                                                                        </div>
                                                                    ) : <span style={styles.emptyValue}>-</span>}
                                                                </td>

                                                                <td style={styles.tableCell}><div className="duration-value" style={styles.durationValue}>{calculateDuration(record.check_in_time, record.check_out_time)}</div></td>
                                                                <td style={styles.tableCell}>{getStatusBadge(record.check_out_time)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>

                                            {/* Pagination */}
                                            {totalPages > 1 && (
                                                <div className="pagination" style={styles.pagination}>
                                                    <button
                                                        style={{
                                                            ...styles.paginationBtn,
                                                            opacity: currentPage === 1 ? 0.6 : 1,
                                                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                                                        }}
                                                        onClick={() => paginate(currentPage - 1)}
                                                        disabled={currentPage === 1}
                                                    >
                                                        Previous
                                                    </button>
                                                    
                                                    <div className="pagination-pages" style={styles.paginationPages}>
                                                        {[...Array(totalPages)].map((_, index) => (
                                                            <button
                                                                key={index}
                                                                style={
                                                                    currentPage === index + 1 
                                                                        ? styles.paginationPageActive 
                                                                        : styles.paginationPage
                                                                }
                                                                onClick={() => paginate(index + 1)}
                                                            >
                                                                {index + 1}
                                                            </button>
                                                        ))}
                                                    </div>
                                                    
                                                    <button
                                                        style={{
                                                            ...styles.paginationBtn,
                                                            opacity: currentPage === totalPages ? 0.6 : 1,
                                                            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                                                        }}
                                                        onClick={() => paginate(currentPage + 1)}
                                                        disabled={currentPage === totalPages}
                                                    >
                                                        Next
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <style jsx>{`
                    .spinning {
                        animation: spin 1s linear infinite;
                    }

                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }

                    @keyframes float {
                        0%, 100% { transform: translate(0px, 0px) scale(1); }
                        33% { transform: translate(30px, -50px) scale(1.1); }
                        66% { transform: translate(-20px, 20px) scale(0.9); }
                    }

                    @keyframes wave {
                        0% { transform: translateX(-100%); }
                        100% { transform: translateX(100%); }
                    }

                    @keyframes bounce {
                        0%, 80%, 100% {
                            transform: scale(0);
                        } 40% {
                            transform: scale(1);
                        }
                    }
                    
                    /* Custom Hover Effects */
                    .stat-card:hover .stat-glow {
                        opacity: 0.15;
                    }
                    .records-table tbody tr:hover {
                        background: rgba(255, 255, 255, 0.05);
                    }

                    /* ADDED: Hover effect for clickable users */
                    .user-info:hover {
                        color: ${colors.accent} !important;
                        transform: translateX(2px);
                        transition: all 0.2s ease;
                    }

                    /* Dropdown specific styles */
                    select {
                        appearance: none;
                        -webkit-appearance: none;
                        -moz-appearance: none;
                    }

                    select:focus {
                        background: rgba(255, 255, 255, 0.15);
                        border-color: ${colors.primary};
                        color: white;
                        box-shadow: 0 0 0 0.2rem ${colors.primary}25;
                        outline: none;
                    }

                    /* Dropdown options styling */
                    select option {
                        background: ${colors.backgroundDark};
                        color: white;
                        border: none;
                        padding: 12px;
                    }

                    select option:checked {
                        background: ${colors.primary};
                        color: white;
                    }

                    select option:hover {
                        background: ${colors.secondary};
                        color: white;
                    }
                `}</style>
            </div>
        </Layout>
    );
}

const styles = {
    container: {
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.backgroundMedium} 50%, ${colors.backgroundLight} 100%)`,
        position: 'relative',
        overflowX: 'hidden'
    },
    animatedBackground: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none'
    },
    floatingOrb: {
        position: 'absolute',
        borderRadius: '50%',
        filter: 'blur(40px)',
        opacity: 0.3,
        animation: 'float 8s ease-in-out infinite'
    },
    orb1: {
        width: '300px',
        height: '300px',
        background: colors.primary,
        top: '10%',
        left: '5%',
        animationDelay: '0s'
    },
    orb2: {
        width: '200px',
        height: '200px',
        background: colors.secondary,
        top: '60%',
        right: '10%',
        animationDelay: '2s'
    },
    orb3: {
        width: '250px',
        height: '250px',
        background: colors.accent,
        bottom: '20%',
        left: '15%',
        animationDelay: '4s'
    },
    orb4: {
        width: '180px',
        height: '180px',
        background: colors.success,
        top: '30%',
        right: '20%',
        animationDelay: '6s'
    },
    gridOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
        backgroundSize: '50px 50px'
    },
    header: {
        padding: '40px 40px 30px',
        position: 'relative',
        zIndex: 2
    },
    headerContent: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        maxWidth: '1400px',
        margin: '0 auto'
    },
    greeting: {
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
        marginBottom: '15px'
    },
    pageTitle: {
        fontSize: '3rem',
        fontWeight: '800',
        background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        margin: 0
    },
    welcomeBadge: {
        background: 'rgba(255, 255, 255, 0.1)',
        padding: '8px 16px',
        borderRadius: '20px',
        border: `1px solid ${colors.borderDark}`,
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    badgeText: {
        color: 'white',
        fontWeight: '600',
        fontSize: '0.9rem'
    },
    badgeRole: {
        background: colors.primary,
        color: 'white',
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '0.7rem',
        fontWeight: '600',
        textTransform: 'uppercase'
    },
    pageSubtitle: {
        color: colors.textLight,
        fontSize: '1.2rem',
        margin: 0,
        maxWidth: '600px'
    },
    headerActions: {
        display: 'flex',
        alignItems: 'center',
        gap: '15px'
    },
    refreshBtn: {
        background: 'rgba(255, 255, 255, 0.1)',
        color: 'white',
        border: `1px solid ${colors.borderDark}`,
        padding: '12px 20px',
        borderRadius: '15px',
        cursor: 'pointer',
        fontWeight: '600',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
    },
    exportBtn: {
        background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
        color: 'white',
        border: 'none',
        padding: '12px 20px',
        borderRadius: '15px',
        cursor: 'pointer',
        fontWeight: '600',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
    },
    refreshIcon: {
        fontSize: '1.1rem'
    },
    exportIcon: {
        fontSize: '1.1rem'
    },
    messageAlert: {
        color: 'white',
        padding: '20px',
        borderRadius: '15px',
        margin: '0 40px 30px',
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        maxWidth: '1400px',
        marginLeft: 'auto',
        marginRight: 'auto'
    },
    alertIcon: {
        fontSize: '1.5rem'
    },
    alertMessage: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        color: 'white'
    },
    alertClose: {
        background: 'none',
        border: 'none',
        color: colors.danger,
        cursor: 'pointer',
        padding: '8px',
        borderRadius: '8px',
        transition: 'background 0.3s ease'
    },
    content: {
        padding: '0 40px 40px',
        maxWidth: '1400px',
        margin: '0 auto',
        position: 'relative',
        zIndex: 2
    },
    statsSection: {
        marginBottom: '50px'
    },
    sectionTitle: {
        marginBottom: '30px',
        color: 'white'
    },
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '25px'
    },
    statCard: {
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '25px',
        padding: '30px',
        position: 'relative',
        overflow: 'hidden',
        border: `1px solid ${colors.borderDark}`,
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        boxShadow: '0 5px 15px rgba(0, 0, 0, 0.3)'
    },
    statGlow: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'inherit',
        filter: 'blur(20px)',
        opacity: 0,
        transition: 'opacity 0.3s ease'
    },
    statContent: {
        position: 'relative',
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        gap: '20px'
    },
    statIcon: {
        width: '80px',
        height: '80px',
        borderRadius: '20px',
        background: 'rgba(255, 255, 255, 0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '2rem',
        color: 'white'
    },
    statInfo: {
        flex: 1
    },
    statNumber: {
        fontSize: '2.5rem',
        fontWeight: '800',
        color: 'white',
        margin: '0 0 5px 0',
        lineHeight: 1
    },
    statLabel: {
        fontSize: '1.1rem',
        fontWeight: '600',
        color: 'white',
        margin: '0 0 5px 0'
    },
    statTrend: {
        color: colors.textLight,
        fontSize: '0.9rem'
    },
    statWave: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: '100%',
        height: '4px',
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)',
        animation: 'wave 2s linear infinite'
    },
    filtersSection: {
        marginBottom: '40px'
    },
    filtersCard: {
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '20px',
        border: `1px solid ${colors.borderDark}`,
        backdropFilter: 'blur(10px)'
    },
    cardHeader: {
        padding: '25px 30px',
        borderBottom: `1px solid ${colors.borderDark}`
    },
    cardTitle: {
        fontSize: '1.5rem',
        fontWeight: '700',
        color: 'white',
        margin: 0
    },
    cardBody: {
        padding: '30px'
    },
    filterGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '20px'
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
    },
    formLabel: {
        color: 'white',
        fontWeight: '600',
        fontSize: '0.9rem'
    },
    formInput: {
        background: 'rgba(255, 255, 255, 0.1)',
        border: `2px solid ${colors.borderDark}`,
        borderRadius: '10px',
        padding: '12px 15px',
        color: 'white',
        fontSize: '1rem',
        transition: 'all 0.3s ease',
        backdropFilter: 'blur(10px)'
    },
    dropdownSelect: {
        background: 'rgba(255, 255, 255, 0.1)',
        border: `2px solid ${colors.borderDark}`,
        borderRadius: '10px',
        padding: '12px 35px 12px 15px',
        color: 'white',
        fontSize: '1rem',
        transition: 'all 0.3s ease',
        backdropFilter: 'blur(10px)',
        appearance: 'none',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
        backgroundPosition: 'right 15px center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: '12px',
        cursor: 'pointer'
    },
    clearFiltersBtn: {
        background: 'rgba(255, 255, 255, 0.1)',
        color: 'white',
        border: `1px solid ${colors.borderDark}`,
        padding: '12px 20px',
        borderRadius: '10px',
        cursor: 'pointer',
        fontWeight: '600',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease'
    },
    recordsTableSection: {
        marginBottom: '40px'
    },
    tableCard: {
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '20px',
        border: `1px solid ${colors.borderDark}`,
        backdropFilter: 'blur(10px)',
        overflow: 'hidden',
        boxShadow: '0 5px 15px rgba(0, 0, 0, 0.3)'
    },
    tableCardHeader: {
        background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
        color: 'white',
        padding: '25px 30px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    tableCardTitle: {
        fontSize: '1.5rem',
        fontWeight: '700',
        margin: 0
    },
    tableInfo: {
        color: 'rgba(255, 255, 255, 0.9)',
        fontSize: '0.9rem'
    },
    tableInfoText: {
        fontWeight: '500'
    },
    tableCardBody: {
        padding: '0'
    },
    loadingState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 40px',
        color: 'white',
        textAlign: 'center'
    },
    loadingAnimation: {
        marginBottom: '30px'
    },
    loadingDots: {
        display: 'flex',
        gap: '8px'
    },
    dot: {
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        background: colors.primary,
        animation: 'bounce 1.4s ease-in-out infinite both'
    },
    loadingText: {
        fontSize: '1.8rem',
        margin: '0 0 12px 0',
        fontWeight: '600'
    },
    loadingSubtext: {
        color: colors.textLight,
        margin: 0,
        fontSize: '1.1rem'
    },
    emptyState: {
        textAlign: 'center',
        padding: '60px 40px',
        color: colors.textLight
    },
    emptyIcon: {
        fontSize: '4rem',
        marginBottom: '20px',
        opacity: 0.5
    },
    emptyTitle: {
        color: 'white',
        marginBottom: '10px'
    },
    emptyText: {
        margin: '0 0 20px 0'
    },
    resetFiltersBtn: {
        background: 'rgba(255, 255, 255, 0.1)',
        color: 'white',
        border: `1px solid ${colors.borderDark}`,
        padding: '10px 20px',
        borderRadius: '10px',
        cursor: 'pointer',
        fontWeight: '600',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease'
    },
    tableContainer: {
        overflowX: 'auto'
    },
    recordsTable: {
        width: '100%',
        borderCollapse: 'collapse'
    },
    tableHeader: {
        background: 'rgba(255, 255, 255, 0.1)',
        color: 'white',
        padding: '15px 12px',
        textAlign: 'left',
        fontWeight: '600',
        fontSize: '0.85rem',
        borderBottom: `2px solid ${colors.borderDark}`,
        whiteSpace: 'nowrap'
    },
    tableRow: {
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        transition: 'background 0.3s ease'
    },
    tableCell: {
        padding: '12px',
        color: 'white',
        fontSize: '0.85rem',
        borderBottom: `1px solid rgba(255, 255, 255, 0.03)`
    },
    userInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
    },
    userName: {
        fontWeight: '600',
        color: 'white'
    },
    userId: {
        fontSize: '0.75rem',
        color: '#a0a0a0'
    },
    roleBadge: {
        color: 'white',
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '0.75rem',
        fontWeight: '600',
        display: 'inline-flex',
        alignItems: 'center'
    },
    timeValue: {
        color: 'rgba(255, 255, 255, 0.9)',
        fontWeight: '500'
    },
    emptyValue: {
        color: 'rgba(255, 255, 255, 0.5)',
        fontStyle: 'italic',
        fontSize: '0.8rem'
    },
    durationValue: {
        color: colors.accent,
        fontWeight: '600'
    },
    dateValue: {
        color: 'rgba(255, 255, 255, 0.8)'
    },
    statusBadgeActive: {
        background: colors.warning,
        color: colors.backgroundDark,
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '0.75rem',
        fontWeight: '600',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px'
    },
    statusBadgeCompleted: {
        background: colors.success,
        color: 'white',
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '0.75rem',
        fontWeight: '600',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px'
    },
    statusDot: {
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        background: 'currentColor'
    },
    pagination: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px 30px',
        borderTop: `1px solid ${colors.borderDark}`
    },
    paginationBtn: {
        background: 'rgba(255, 255, 255, 0.1)',
        color: 'white',
        border: `1px solid ${colors.borderDark}`,
        padding: '8px 16px',
        borderRadius: '8px',
        cursor: 'pointer',
        fontWeight: '500',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease'
    },
    paginationPages: {
        display: 'flex',
        gap: '8px'
    },
    paginationPage: {
        background: 'rgba(255, 255, 255, 0.1)',
        color: 'white',
        border: `1px solid ${colors.borderDark}`,
        padding: '8px 12px',
        borderRadius: '6px',
        cursor: 'pointer',
        fontWeight: '500',
        minWidth: '40px',
        transition: 'all 0.3s ease'
    },
    paginationPageActive: {
        background: colors.primary,
        color: 'white',
        border: `1px solid ${colors.primary}`,
        padding: '8px 12px',
        borderRadius: '6px',
        cursor: 'pointer',
        fontWeight: '500',
        minWidth: '40px'
    },
    accessDeniedContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        textAlign: 'center',
        color: 'white'
    },
    accessDeniedIcon: {
        fontSize: '4rem',
        marginBottom: '20px',
        opacity: 0.7
    },
    accessDeniedTitle: {
        fontSize: '2rem',
        marginBottom: '15px',
        fontWeight: '600'
    },
    accessDeniedText: {
        fontSize: '1.1rem',
        opacity: 0.8,
        maxWidth: '400px'
    },
    locationValue: {
        fontSize: '0.8rem',
        opacity: 0.9,
        color: '#ccc',
        whiteSpace: 'nowrap'
    },
    deviceValue: {
        fontSize: '0.75rem',
        fontWeight: '500',
        color: colors.accent,
        opacity: 0.7
    },
    browserInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    browserIcon: {
        fontSize: '1rem'
    },
    browserText: {
        fontSize: '0.75rem',
        fontWeight: '500',
        color: colors.textLight
    }
};

export default CheckInOutRecords;